import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal.jsx'
import DebugAccordion from '../../components/DebugAccordion'
import Loadable from '../../components/Loadable.jsx'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import ProfileNav from './components/ProfileNav'
import ProfileTools from './components/ProfileTools.jsx'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchProfile, updateProfile } from '../../state/actions/profileActions'
import { fetchProfileSchema } from '../../state/actions/profileSchemaActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'
import { fetchUtg } from '../../state/actions/utgActions.js'

import { profileSignal, profileLoadedSignal, profileErrorSignal, profileCcSchemaSignal } from '../../state/signals/profileSignals'
import { profileSchemaSignal } from '../../state/signals/profileSchemaSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ProfileRawEdit() {

    const navigate = useNavigate()
    const { beId, profileId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/profiles`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTG profiles for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })

    useEffect(() => {
        if (beIdChanged) return
        fetchProfile(beId, profileId, config, accessToken).then(
            () => {
                fetchSchemaManifest().then(
                    fetchProfileSchema(beId, profileCcSchemaSignal.value, schemaManifestSignal.value, config, accessToken)
                )
            }
        )
    }, [beId, beIdChanged, profileId, config, accessToken])

    const editableProfileSignal = useSignal('')
    const profileRowsComputed = useComputed(() => {
        if (!profileLoadedSignal.value) {
            return 0
        }
        let text = JSON.stringify(profileSignal.value.config, null, 4)
        let textArray = text.split('\n')
        return textArray.length + 1
    })

    const showCancelModalSignal = useSignal(false)
    const accordionData = [
        { name: 'Original Profile Data', data: profileSignal.value },
        { name: 'Profile Schema', data: profileSchemaSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value }
    ]

    const handleFormSubmit = () => {
        try {
            // build the full config data including apiInterface and deviceConfig data to save back to server
            let data = {
                id: profileSignal.value.id,
                name: profileSignal.value.name,
                businessEntityId: profileSignal.value.businessEntityId,
                config: JSON.parse(editableProfileSignal.value),
            }

            //console.log(data)
            updateProfile(beId, profileId, data, config, accessToken, true)
                .then(() => {
                    addNotification('Profile was successfully updated.', NotificationTypes.SUCCESS, true)
                })
                .catch(error => {
                    console.log(error.toString())
                    addNotification(`Unable to update the Profile. ${error.toString()}`, NotificationTypes.ERROR, false)
                    return false
                })
                .finally(() => {
                    // must return false to avoid postback
                    return false
                })
            
        }
        catch (error) {
            addNotification(`Unable to update the Profile. ${error.toString()}`, NotificationTypes.ERROR, false)
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    return (
        <>
            <ConfirmationModal show={showCancelModalSignal.value} handleCancel={() => showCancelModalSignal.value = false} handleConfirm={() => navigate('../')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                    Profile List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    profileLoadedSignal.value ? <>Manage {profileSignal.value.name}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title='Profile Edit JSON' canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[profileLoadedSignal]} errorSignals={[profileErrorSignal]}>
                    <ProfileTools profile={profileSignal.value}></ProfileTools>
                </Loadable>
            </Stack>
            <ProfileNav activeKey="5"></ProfileNav>
            <Form onSubmit={handleFormSubmit}>
                <Form.Group className="mb-3" controlId="formEditJson">
                    <Form.Label>Profile JSON</Form.Label>
                    <Form.Control as="textarea" rows={profileRowsComputed.value} onChange={(e) => editableProfileSignal.value = e.target.value} defaultValue={JSON.stringify(profileSignal.value?.config, null, 4)} readOnly={!profileLoadedSignal.value} />
                </Form.Group>
                {canEditComputed.value && <Button variant="primary" type="submit">
                    Submit
                </Button>}
            </Form>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}